import {
    ElectionDefinition,
    getElectionId,
    GetRouteAdTargeting,
    getSocialImageMeta,
    PageType,
} from '@news-mono/web-common'
import { layout } from '../../../App.routing'
import {
    metrics,
    calcRem,
    theWestTextOnlySectionHeaderOverride,
    getFullStateName,
} from '@news-mono/component-library'
import { TheWestSection } from '@news-mono/common'
import { getElection2025Topic } from './createElectionLandingPage'

type CreateElectionPartyTotalsPageArgs = {
    getAdTargeting: GetRouteAdTargeting
    electionDefinition: ElectionDefinition
    heading?: string
}

export const createElectionPartyTotalsPage = ({
    getAdTargeting,
    electionDefinition: { election, logo, meta },
    heading,
}: CreateElectionPartyTotalsPageArgs): PageType<TheWestSection> => {
    let collectionHeading
    let description
    if (election.type === 'federal') {
        heading ??= `${election.year} Federal Election Party Totals`
        description = `All the party results for the ${election.year} Federal election. See how the Australian Labor Party, the Liberal Party, the Nationals, the Greens and the independents all stacked up across Australia.`
        collectionHeading = `Latest Federal Election Updates`
    } else {
        heading ??= `${
            election.year
        } ${election.state.toUpperCase()} State Election Party Totals`
        description = `All the party results for the ${
            election.year
        } ${getFullStateName(
            election.state,
        )} State election. See how the Australian Labor Party, the Liberal Party, the Nationals, the Greens and the independents all stacked up in ${election.state.toUpperCase()}.`
        collectionHeading = `Latest ${election.state.toUpperCase()} Election Updates`
    }

    return {
        kind: 'page',
        heading,
        pageType: 'static',
        section: 'default',
        hideHeading: true,
        adTargeting: getAdTargeting(
            'home',
            'default',
            getElection2025Topic(election),
        ),
        pageMeta: {
            title: heading,
            description,
            meta: meta.partyTotalsSocialImage
                ? getSocialImageMeta(
                      meta.partyTotalsSocialImage,
                      '2400',
                      '1260',
                  )
                : undefined,
        },
        socialMeta: {
            title: heading,
            description,
        },
        compositions: [
            layout.composition({
                type: 'box',
                props: {
                    containerWidth:
                        metrics.thewest.siteMetrics.mainContentWidth,
                    flex: {
                        default: {
                            flexDirection: 'column',
                            gap: calcRem(24),
                        },
                        sm: {
                            gap: calcRem(48),
                        },
                    },
                    verticalGutters: ['unset', 'xxl'],
                },
                contentAreas: {
                    main: [
                        layout.nestedComposition({
                            type: 'box',
                            props: {},
                            contentAreas: {
                                main: [
                                    layout.component({
                                        type: 'breaking-news',
                                        props: {
                                            dataDefinitionArgs: {
                                                type: 'curation',
                                                name: 'breaking-news',
                                                offset: 0,
                                                pageSize: 1,
                                            },
                                            isElectionPageRoute: true,
                                            removeBottomMargin: true,
                                        },
                                    }),
                                    layout.component({
                                        type: 'election-navigation',
                                        props: {
                                            election,
                                            electionLogo: logo,
                                            activePage: 'party-totals',
                                        },
                                    }),
                                ],
                            },
                        }),
                        layout.nestedComposition({
                            type: 'athena',
                            props: {
                                containerWidth:
                                    metrics.thewest.siteMetrics
                                        .mainContentWidth,
                                elementType: 'div',
                                sidebarOptions: 'hiddenMobile',
                                hasHorizontalGutters: false,
                                hasVerticalPadding: false,
                            },
                            contentAreas: {
                                sidebar: [
                                    layout.nestedComposition({
                                        type: 'box',
                                        props: {
                                            stickyOffset: 350,
                                            fillContainer: true,
                                        },
                                        contentAreas: {
                                            main: [
                                                layout.component({
                                                    type: 'ad-unit',
                                                    props: {
                                                        noticePosition:
                                                            'above-center',
                                                        padding: [
                                                            0,
                                                            0,
                                                            metrics.thewest
                                                                .margins.md,
                                                            0,
                                                        ],
                                                        slot: {
                                                            id: 'sidebar-top',
                                                            size: 'desktopMrecHalfPage',
                                                            pageType: 'static',
                                                        },
                                                        adType: 'inline',
                                                    },
                                                }),
                                                layout.component({
                                                    type: 'juliet',
                                                    props: {
                                                        cardType: {
                                                            type: 'landscape',
                                                            format: 'landscape-smallImage',
                                                        },
                                                        removeHorizontalGutters:
                                                            true,
                                                        kickerMode: 'hidden',
                                                        contentIsTangential:
                                                            true,
                                                        sectionHeader: {
                                                            heading:
                                                                collectionHeading,
                                                        },
                                                        dataDefinitionArgs: {
                                                            type: 'curation',
                                                            name: getElectionId(
                                                                election,
                                                            ),
                                                            pageSize: 4,
                                                            offset: 0,
                                                        },
                                                    },
                                                }),
                                            ],
                                        },
                                    }),
                                ],

                                main: [
                                    layout.nestedComposition({
                                        type: 'component-overrider',
                                        props: {
                                            override: () =>
                                                theWestTextOnlySectionHeaderOverride,
                                        },
                                        contentAreas: {
                                            children: [
                                                layout.component({
                                                    type: 'section-header',
                                                    props: {
                                                        heading: 'Party Totals',
                                                        isStandalonePage: true,
                                                    },
                                                }),
                                            ],
                                        },
                                    }),
                                    layout.component({
                                        type: 'election-party-totals',
                                        props: {
                                            dataDefinitionArgs: {
                                                type: 'election',
                                                electionId: 632,
                                                pageSize: 1,
                                            },
                                            election,
                                            description,
                                        },
                                    }),
                                ],
                            },
                        }),
                    ],
                },
            }),
        ],
    }
}
